import { useCustom } from '@refinedev/core';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UI_STRINGS } from './locale';

/**
 * Данные статуса языка от бэкенда (из translation-status.service.ts)
 */
export interface LanguageTranslationStatusItem {
  languageCode: string;
  filledPercent: number;
  filledFields: string[];
  missingFields: string[];
  isComplete: boolean;
}

export interface EntityTranslationStatus {
  entityType: string;
  entityId: number;
  translatableFields: string[];
  languages: LanguageTranslationStatusItem[];
}

interface LanguageTabItem {
  code: string;
  label: string;
  flagEmoji?: string;
}

interface LanguageTabsProps {
  /** Доступные языки */
  languages: LanguageTabItem[];
  /** Активный язык */
  activeLanguage: string;
  /** Колбэк смены языка */
  onLanguageChange: (code: string) => void;
  /** Тип сущности для загрузки статуса переводов (необязателен) */
  entityType?: string;
  /** ID сущности для загрузки статуса переводов */
  entityId?: number;
  className?: string;
}

/**
 * Горизонтальные табы языков для форм с переводами.
 * Если переданы entityType + entityId — загружает статус заполненности переводов
 * через GET /translations/status/:entityType/:entityId.
 * Показывает зелёную галочку если язык заполнен полностью.
 */
export function LanguageTabs({
  languages,
  activeLanguage,
  onLanguageChange,
  entityType,
  entityId,
  className,
}: LanguageTabsProps) {
  const hasEntityInfo = entityType !== undefined && entityId !== undefined;

  const { result: statusResult } = useCustom<EntityTranslationStatus>({
    url: `/translations/status/${entityType}/${entityId}`,
    method: 'get',
    queryOptions: {
      enabled: hasEntityInfo,
      staleTime: 30_000,
    },
  });

  const statusData = statusResult?.data as EntityTranslationStatus | undefined;

  const getLanguageStatus = (code: string): LanguageTranslationStatusItem | undefined => {
    if (!statusData?.languages) {
      return undefined;
    }
    return statusData?.languages?.find((l) => l.languageCode === code);
  };

  return (
    <div className={cn('flex items-center gap-1 flex-wrap', className)}>
      {languages.map((lang) => {
        const status = getLanguageStatus(lang.code);
        const isActive = lang.code === activeLanguage;
        const isComplete = status?.isComplete === true;
        const hasPartial = status !== undefined && !isComplete && status.filledFields.length > 0;

        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => onLanguageChange(lang.code)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
              'border transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
              isActive
                ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
                : 'bg-background border-input',
            )}
          >
            {lang.flagEmoji && <span aria-hidden="true">{lang.flagEmoji}</span>}
            <span>{lang.label}</span>
            {isComplete && (
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  isActive ? 'text-primary-foreground' : 'text-green-500',
                )}
                aria-label={UI_STRINGS.filled}
              />
            )}
            {hasPartial && (
              <span
                className={cn(
                  'text-[10px] font-normal',
                  isActive
                    ? 'text-primary-foreground/80'
                    : 'text-amber-600 dark:text-amber-400',
                )}
              >
                {status.filledFields.length}
                /
                {status.filledFields.length + status.missingFields.length}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
